import React from 'react';
import ReactDOM from 'react-dom';
import { message } from 'antd';
import './agentLanding/AgentLanding.css';
import reportWebVitals from '../reportWebVitals';

const INSTALL_DOC_URL = 'https://i.yuxie.vip/docs/yuxie-agent-install.md';
const INSTALL_PROMPT = `帮我安装语写 Agent。请直接打开并读取正文，不要使用搜索结果摘要：${INSTALL_DOC_URL}`;
const AGENT_TOOLS = [
  { mark: 'CX', name: 'Codex' },
  { mark: 'CC', name: 'Claude Code' },
  { mark: 'CU', name: 'Cursor' },
  { mark: 'TR', name: 'Trae' },
];
const FEATURES = [
  { icon: 'search', title: '查询时间记录', desc: '按开始日期、结束日期、标签和关键词查询自己的时间记录。' },
  { icon: 'chart', title: '生成复盘报告', desc: '让 AI 汇总周报、时间分布、事项模式和可复盘线索。' },
  { icon: 'refresh', title: '本地缓存同步', desc: '安装包会缓存查询结果，后续按缺口同步，减少重复拉取。' },
  { icon: 'key', title: '统一授权入口', desc: '当前先接入时间记录，后续同一个 Agent 承载更多语写能力。' },
];
const FAQS = [
  {
    question: '为什么现在只提供 AI 安装？',
    answer: '现阶段主路径是把一行提示词交给自己的 AI，AI 按统一安装文档执行一键安装脚本、打开授权页并完成本机安装，普通用户不需要理解额外参数。',
  },
  {
    question: '需要自己创建 API Key 吗？',
    answer: '安装时不需要。用户登录授权后，系统会自动生成 Agent Skill 专用访问凭证，并保存到本机配置。API Key 页面主要用于查看、重命名、撤销和高级脚本使用。',
  },
  {
    question: '安装页提示“安装参数缺失”怎么办？',
    answer: '说明直接打开了裸的 agent-install 页面。请回到 AI 安装流程，让 AI 重新执行安装步骤，生成带 redirect_uri 和 state 的授权链接。',
  },
  {
    question: '授权完成后为什么 AI 还不能识别 skill？',
    answer: '请先重启 Codex、Claude Code、Cursor 或 Trae。安装器会把 skill 写入本机技能目录，有些 Agent 需要重启后才会重新扫描。',
  },
  {
    question: '语写 Agent 能读取哪些数据？',
    answer: '当前只接入用户自己的时间记录查询，支持日期范围、标签、关键词和分页。后续会在同一个授权入口下扩展更多个人数据能力。',
  },
  {
    question: '授权后可以撤销吗？',
    answer: '可以。在控制台的 API Key 页面撤销对应的 Agent Skill 凭证后，已安装的本机配置会失效，需要重新授权才能继续访问。',
  },
];

function Icon({ name }) {
  const icons = {
    copy: <><rect x="9" y="9" width="10" height="10" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-5" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></>,
    chart: <><path d="M3 3v18h18" /><path d="m7 14 4-4 3 3 5-7" /></>,
    key: <><circle cx="7.5" cy="14.5" r="5.5" /><path d="M12 10 22 0" /><path d="M17 5 19 7" /><path d="M14 8 16 10" /></>,
    refresh: <><path d="M21 12a9 9 0 0 1-15.3 6.4" /><path d="M3 12A9 9 0 0 1 18.3 5.6" /><path d="M18 2v4h-4" /><path d="M6 22v-4h4" /></>,
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
}

class AgentLanding extends React.Component {
  copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(INSTALL_PROMPT);
      message.success('已复制一行提示词');
    } catch (e) {
      message.warning('复制失败，请手动复制');
    }
  };

  render() {
    return (
      <main className="agent-landing">
        <nav className="agent-nav">
          <a className="agent-brand" href="/">
            <span className="agent-brand-mark">语</span>
            <span>语写 Agent</span>
          </a>
          <div className="agent-nav-actions">
            <a href="/docs/yuxie-agent-install.md">安装文档</a>
            <a href="/user-key.html">用户中心</a>
          </div>
        </nav>

        <section className="agent-hero">
          <div className="agent-hero-copy">
            <div className="agent-eyebrow">当前支持时间记录，后续接入更多个人数据能力</div>
            <h1>让你的 AI 读取自己的语写数据</h1>
            <p>
              语写 Agent 是给个人 AI 使用的数据入口。用户登录授权后，AI 可以读取自己的数据，
              当前可查询时间记录，用于复盘、搜索、总结和导出。
            </p>
            <div className="agent-cta-row">
              <button className="agent-primary" onClick={this.copyPrompt}>
                <Icon name="copy" />
                复制一行提示词
              </button>
              <a className="agent-secondary" href="/docs/yuxie-agent-install.md">
                查看安装文档
              </a>
            </div>
            <pre className="agent-prompt">{INSTALL_PROMPT}</pre>
            <p className="agent-install-note">
              当前只开放 AI 安装路径。把这行提示词发给自己的 Agent，它会执行一键安装脚本并引导登录授权。
            </p>
          </div>

          <div className="agent-snapshot" aria-label="语写 Agent 数据查询示例">
            <div className="snapshot-toolbar">
              <span />
              <span />
              <span />
              <strong>time-query</strong>
            </div>
            <div className="snapshot-body">
              <div className="snapshot-line dim">$ node bin/originsite-agent.js time-query --start 2026-05-01 --end 2026-05-27</div>
              <div className="snapshot-card">
                <div>
                  <span>记录天数</span>
                  <strong>27</strong>
                </div>
                <div>
                  <span>总时长</span>
                  <strong>86.5h</strong>
                </div>
                <div>
                  <span>分页</span>
                  <strong>hasMore</strong>
                </div>
              </div>
              <div className="snapshot-line">AI: 本周开发时间集中在上午，会议占比 18%。</div>
              <div className="snapshot-line">AI: 已按标签整理出可复盘事项。</div>
            </div>
          </div>
        </section>

        <section className="agent-section agent-tools">
          <h2>支持主流 AI Agent 工具</h2>
          <div className="agent-tool-list" aria-label="支持的 AI Agent 工具">
            {AGENT_TOOLS.map((tool) => (
              <div className="agent-tool" key={tool.name}>
                <span>{tool.mark}</span>
                <strong>{tool.name}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="agent-section">
          <div className="agent-section-title">
            <h2>能做什么</h2>
            <p>安装一次，后续由用户自己的 AI 通过统一入口读取授权数据。</p>
          </div>
          <div className="agent-feature-grid">
            {FEATURES.map((feature) => (
              <article key={feature.title}>
                <Icon name={feature.icon} />
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="agent-section agent-flow">
          <div className="agent-section-title">
            <h2>安装流程</h2>
            <p>用户不需要手动创建参数，AI 按统一文档执行一键安装脚本并引导登录授权。</p>
          </div>
          <ol>
            <li><span>1</span><strong>复制一行提示词</strong><p>把页面上的一行提示词发给 Claude Code、Codex、Cursor 或 Trae。</p></li>
            <li><span>2</span><strong>AI 完成安装</strong><p>AI 执行一键安装脚本，并打开带参数的授权页。</p></li>
            <li><span>3</span><strong>用户登录授权</strong><p>用户完成登录后，本机保存凭证并安装 Agent Skill。</p></li>
          </ol>
        </section>

        <section className="agent-section agent-faq">
          <div className="agent-section-title">
            <h2>常见问题</h2>
            <p>围绕当前一键安装、授权凭证和时间记录查询的真实使用问题。</p>
          </div>
          <div className="agent-faq-list">
            {FAQS.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="agent-security">
          <Icon name="shield" />
          <div>
            <h2>安全边界</h2>
            <p>
              不需要把账号密码交给 AI。授权完成后只在本机保存访问凭证，用户可以在 API Key 页面撤销。
              如果安装页提示参数缺失，说明打开了裸链接，需要重新执行安装命令生成带参数的授权页。
            </p>
          </div>
        </section>
      </main>
    );
  }
}

ReactDOM.render(<AgentLanding />, document.getElementById('root'));

reportWebVitals();
